"use client";

import { useLazyQuery } from "@apollo/client/react";
import { identity } from "deso-protocol";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useContext, useEffect, useState } from "react";
import { FiLogOut, FiUser, FiUserPlus } from "react-icons/fi";

import DesoAvatar from "@/components/shared/deso-avatar";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerSeparator,
  DropdownDrawerSub,
  DropdownDrawerSubContent,
  DropdownDrawerSubTrigger,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeedContext } from "@/contexts/feed-context";
import { UserContext } from "@/contexts/user-context";
import { AccountByPublicKeyDocument } from "@/graphql/codegen/graphql";
import { useMobile } from "@/hooks/use-mobile";
import { isAbortError, shortenLongWord } from "@/utils/helpers";
import type { PartialAccount } from "@/utils/types";
import { getUserDisplayName, getUserName } from "@/utils/usernames";

import CopyToClipboard from "./copy-to-clipboard";

type UserDropdownProps = {
  dropdownAlign?: "start" | "center" | "end";
  avatarSize?: number;
  showUsername?: boolean;
};

export function UserDropdown({
  dropdownAlign = "end",
  avatarSize = 40,
  showUsername = true,
}: UserDropdownProps) {
  const {
    user,
    loading: loadingUser,
    alternateUsers,
  } = useContext(UserContext);
  const pathname = usePathname();
  const { isMobile } = useMobile();
  const { resetFeed } = useFeedContext();

  const [open, setOpen] = useState(false);
  const [visibleAlternateUsers, setVisibleAlternateUsers] =
    useState(alternateUsers);

  const [getAccountByPublicKey] = useLazyQuery(AccountByPublicKeyDocument);

  const [otherAccounts, setOtherAccounts] = useState<
    Array<PartialAccount & { publicKey: string }>
  >([]);

  useEffect(() => {
    setVisibleAlternateUsers(alternateUsers);
  }, [alternateUsers]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    // The identity subscriber can lag behind storage changes from popup flows,
    // so re-read the same source `deso-protocol` uses when the switcher opens.
    void Promise.resolve(identity.snapshot())
      .then((snapshot) => {
        if (!cancelled) {
          setVisibleAlternateUsers(snapshot.alternateUsers || {});
        }
      })
      .catch((error: unknown) => {
        console.error("Failed to refresh identity snapshot", error);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    let cancelled = false;

    const fetchOtherAccounts = async () => {
      const alternateUserKeys = Object.keys(visibleAlternateUsers || {});

      if (!alternateUserKeys.length) {
        setOtherAccounts([]);
        return;
      }

      const otherAccountRequests = alternateUserKeys.map((e) => {
        const request = getAccountByPublicKey({
          variables: {
            publicKey: e,
          },
        });
        request.retain?.();
        return request;
      });

      const otherAccounts = await Promise.allSettled(otherAccountRequests);

      if (cancelled) {
        return;
      }

      otherAccounts.forEach((result) => {
        if (result.status === "rejected" && !isAbortError(result.reason)) {
          console.error(result.reason);
        }
      });

      setOtherAccounts(
        otherAccounts.map((result, index) =>
          result.status === "fulfilled" &&
          result.value.data?.accountByPublicKey?.publicKey
            ? result.value.data.accountByPublicKey
            : {
                publicKey: alternateUserKeys[index],
              },
        ),
      );
    };

    void fetchOtherAccounts();

    return () => {
      cancelled = true;
    };
  }, [getAccountByPublicKey, visibleAlternateUsers]);

  // During onboarding we don't want to show the user dropdown since it can cause issues with the onboarding flow if the
  // user can switch accounts or logout while onboarding.
  if (pathname.startsWith("/sign-up")) {
    return null;
  }

  if (loadingUser) {
    return (
      <div className="flex gap-3 p-3">
        <Skeleton
          className="rounded-full"
          style={{
            width: avatarSize,
            height: avatarSize,
          }}
        />
        <div
          className="
            hidden h-10 grow flex-col gap-2
            sm:flex
          "
        >
          <Skeleton className="h-4 w-[120px]" />
          <Skeleton className="h-4 w-[120px]" />
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const renderUserName = () => {
    return user
      ? getUserName({
          account: user,
        })
      : "";
  };
  const profileHref = `/${user.username || user.publicKey}`;

  return (
    <DropdownDrawer
      historyName="user-account"
      onOpenChange={setOpen}
      open={open}
      title="Account"
    >
      <DropdownDrawerTrigger
        render={
          <section
            className="
              flex w-full cursor-pointer flex-row items-center gap-2 p-3
            "
          >
            <DesoAvatar
              className="cursor-pointer border"
              account={user}
              clickable={false}
              size={avatarSize}
            />
            {!loadingUser && user && showUsername && (
              <div className="order-2 flex flex-col gap-1">
                <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                  {getUserDisplayName(user) ? (
                    <span
                      className="
                        pr-4 font-sans font-semibold
                        hover:text-primary
                      "
                    >
                      {getUserDisplayName({ ...user, truncateLength: 13 })}
                    </span>
                  ) : (
                    <span className="text-xs text-muted">Logged in as:</span>
                  )}
                  <span className="inline-block max-w-[100px] truncate text-muted">
                    {renderUserName()}
                  </span>
                </div>
              </div>
            )}
          </section>
        }
      />
      <DropdownDrawerContent className="md:w-56" align={dropdownAlign}>
        <div
          className="
            mb-3 rounded-(--dropdown-drawer-section-radius,var(--radius))
            border border-border-light bg-card p-4 text-muted
            md:mb-0 md:rounded-none md:border-0 md:bg-transparent md:px-2
            md:py-1.5 md:text-sm md:font-semibold
          "
        >
          <span className="text-xs">Logged in as:</span>
          <br />
          <span className="font-sans text-muted-foreground">
            {getUserDisplayName(user)}
          </span>
          <div
            className="
              mt-2 flex flex-col justify-between gap-2 font-normal
              hover:text-inherit
            "
          >
            <CopyToClipboard
              iconSize={12}
              text={renderUserName()}
              className="text-sm"
            >
              {renderUserName()}
            </CopyToClipboard>
            <CopyToClipboard
              iconSize={12}
              text={user.publicKey}
              className="text-sm"
            >
              {shortenLongWord(user.publicKey)}
            </CopyToClipboard>
          </div>
        </div>

        <DropdownDrawerItem
          closeOnClick={false}
          render={
            <Link
              href={profileHref}
              onClick={() => setOpen(false)}
              prefetch={false}
              replace={isMobile}
            >
              <FiUser className="mr-2 size-4" />
              <span>View Profile</span>
            </Link>
          }
        />

        <DropdownDrawerSub>
          <DropdownDrawerSubTrigger>
            <FiUserPlus className="mr-2 size-4" />
            <span>Switch Wallets</span>
          </DropdownDrawerSubTrigger>

          <DropdownDrawerSubContent title="Switch Wallets">
            <DropdownDrawerItem
              onClick={async () => {
                await identity.login();
                resetFeed();
              }}
            >
              <FiUser className="mr-2 size-4" />
              <span>Add account</span>
            </DropdownDrawerItem>

            {otherAccounts.length > 0 && (
              <>
                <DropdownDrawerSeparator />

                {otherAccounts.map((e) => {
                  const publicKey = e?.publicKey;

                  if (!e || !publicKey) {
                    return null;
                  }

                  const displayName = getUserDisplayName(e);

                  return (
                    <DropdownDrawerItem
                      key={e.publicKey}
                      onClick={async () => {
                        await identity.setActiveUser(publicKey);
                        resetFeed();
                      }}
                    >
                      <DesoAvatar account={e} clickable={false} />

                      <span className="ml-2">{displayName}</span>
                    </DropdownDrawerItem>
                  );
                })}
              </>
            )}
          </DropdownDrawerSubContent>
        </DropdownDrawerSub>

        <DropdownDrawerSeparator />

        <DropdownDrawerItem
          onClick={async () => {
            await identity.logout();
            resetFeed();
          }}
        >
          <FiLogOut className="mr-2 size-4" />
          <span>Log out</span>
        </DropdownDrawerItem>
      </DropdownDrawerContent>
    </DropdownDrawer>
  );
}
